import { useCallback, useMemo, useRef, useState } from "react";
import { Entity } from "@common/types/entity";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { Drawer } from "@design-system/Drawer";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useEntities } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import { CoaMappingDrawer } from "./CoaMappingDrawer";
import styles from "./CoaMappingDrawer.module.scss";

interface InstanceProps {
    entity: Entity;
    workspaceId: string;
    nextTaskId: string | undefined;
    isOpen: boolean;
    closeOverlay: () => void;
}

// Keyed on the overlay instance by the container, so the prompt state below
// starts fresh on every open.
const CoaMappingDrawerInstance = ({
    entity,
    workspaceId,
    nextTaskId,
    isOpen,
    closeOverlay,
}: InstanceProps) => {
    // Chart edits are saved live; the prompt only says so, once a write landed.
    const [hasChartEdits, setHasChartEdits] = useState(false);
    const [isExitPromptOpen, setIsExitPromptOpen] = useState(false);
    const escapeInterceptorRef = useRef<(() => void) | null>(null);
    const markChartEdited = useCallback(() => setHasChartEdits(true), []);
    const requestClose = useCallback(() => {
        if (hasChartEdits) {
            setIsExitPromptOpen(true);
            return;
        }
        closeOverlay();
    }, [hasChartEdits, closeOverlay]);

    return (
        <Drawer
            size="fullScreen"
            headerAddonLeft={
                <span className={styles.drawerHeader}>
                    Map Chart of Accounts for
                    <EntityAvatar entity={entity} size="xs" aria-hidden />
                    {entity.name}
                </span>
            }
            onClose={closeOverlay}
            onCloseAttempt={(cancel, reason) => {
                const interceptEscape = escapeInterceptorRef.current;
                if (reason === "escape-key" && interceptEscape) {
                    cancel();
                    interceptEscape();
                    return;
                }
                if (hasChartEdits) {
                    cancel();
                    setIsExitPromptOpen(true);
                }
            }}
            open={isOpen}
            noPadding
        >
            <CoaMappingDrawer
                workspaceId={workspaceId}
                entityId={entity.id}
                nextTaskId={nextTaskId}
                onClose={requestClose}
                onChartEdited={markChartEdited}
                escapeInterceptorRef={escapeInterceptorRef}
            />
            <ConfirmationModal
                show={isExitPromptOpen}
                onHide={() => setIsExitPromptOpen(false)}
                onReject={() => setIsExitPromptOpen(false)}
                onConfirm={() => {
                    setIsExitPromptOpen(false);
                    closeOverlay();
                }}
                title="Leave account mapping?"
                question={
                    "Your chart of accounts edits are saved and stay in place. " +
                    "The mapping you've done so far is saved too; open Map " +
                    "accounts again to finish it."
                }
                yes="Leave"
                no="Stay"
            />
        </Drawer>
    );
};

export const CoaMappingDrawerContainer = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo("coaMapping");

    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const entity = useMemo(
        () => entities.find((e) => e.id === overlayInfo?.data?.entityId),
        [entities, overlayInfo?.data?.entityId],
    );
    const workspaceId = overlayInfo?.data?.connectionWorkspaceId;

    if (!overlayInfo?.data || !entity || !workspaceId) {
        return null;
    }

    return (
        <CoaMappingDrawerInstance
            key={overlayInfo.instanceId}
            entity={entity}
            workspaceId={workspaceId}
            nextTaskId={overlayInfo.data.nextTaskId}
            isOpen={!overlayIsClosing}
            closeOverlay={closeOverlay}
        />
    );
};
